import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadMeeting } from '../connection/meeting';
import * as HomeActions from '../stores/actions/home';
import { ChannelTeam, HOME_ROUTES } from '../stores/reducers/home';
import { APP } from '../stores/session-status';
import { GreenroomState } from '../stores/types/types';
import { getFullAssetURI } from '../utils/utils';
import useJoin from './use-join';

const useHome = () => {
	const dispatch = useDispatch();
	const events = useSelector(
		(state: GreenroomState) => state.ContentReducer.events,
	);
	const selectedChannel = useSelector(
		(state: GreenroomState) => state.HomeReducer.selectedChannel,
	);
	const teams = useSelector(
		(state: GreenroomState) => state.HomeReducer.teams,
	);
	const token = useSelector(
		(state: GreenroomState) => state.SessionReducer.token,
	);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const user = useSelector(
		(state: GreenroomState) => state.SessionReducer.user,
	);
	const host = useSelector(
		(state: GreenroomState) => state.SessionReducer.host,
	);

	const { getCurrentOrNewMeeting } = useJoin();

	const [showDetailsButtonDisabled, setDetailsButtonDisabled] = useState<boolean>(false);

	const filteredMeetingsByChannel = (): any[] => {
		let filteredMeetings = JSON.parse(JSON.stringify(events));
		if (selectedChannel) {
			filteredMeetings = filteredMeetings
				.filter(
					(event: any) => event?.channel === selectedChannel?.channel
						|| event?.client === selectedChannel?.channel,
				)
				.map((event: any) => ({
					...event,
					channel_name: selectedChannel.name,
					logo_white: getFullAssetURI(selectedChannel.logo_white),
					logo_color: getFullAssetURI(selectedChannel.logo_color),
				}));
		}
		return filteredMeetings;
	};

	async function handleEnterShowDetails(meeting: any) {
		if (!token) return;

		let meetingToJoin;

		/*
			For all of our apps except Standalone GR when a producer clicks to
			join they are either creating a new meeting that doesn't exist or
			updating an existing meeting to keep necessary fields up to date
			for things like rehearsal emails, etc. Standalone GRs don't create
			or update a meeting here because each row is the actual meeting
			already and presenters/watchers don't create or update a meeting
			because it will already exist for them.
		*/
		const meetingNumber = meeting?.meeting;

		if (
			host
			&& appSignedInWith !== APP.GREENROOM
			&& !meetingNumber
		) {
			// we disable the button if the event is being created to prevent duplicates
			setDetailsButtonDisabled(true);
		}

		if (meetingNumber) {
			// load the meeting in case it has been updated
			meetingToJoin = await LoadMeeting(token, meetingNumber);
		} else {
			const createdMeeting = await getCurrentOrNewMeeting(
				token,
				meeting,
				appSignedInWith,
			);

			if (createdMeeting?.meeting) {
				meetingToJoin = await LoadMeeting(
					token,
					createdMeeting.meeting,
				);
			}
		}

		if (!meetingToJoin) {
			return;
		}

		if (meetingToJoin.settings_json) {
			const { entryWithoutProducer } = JSON.parse(meetingToJoin.settings_json);

			meetingToJoin.entry_without_producer = entryWithoutProducer === undefined
				? true
				: entryWithoutProducer;
		} else {
			meetingToJoin.entry_without_producer = true;
		}

		if (showDetailsButtonDisabled) {
			setDetailsButtonDisabled(false);
		}

		dispatch(HomeActions.setHomeMeeting(meetingToJoin));
		dispatch(HomeActions.setHomeRoute(HOME_ROUTES.DETAILS));
	}

	const baseTeam = useMemo((): ChannelTeam => ({
		channel: selectedChannel?.channel ?? 0,
		appDataType: appSignedInWith,
		teamMembers: [{
			email: user?.email ?? '',
			first_name: user?.first_name ?? '',
			last_name: user?.last_name ?? '',
			avatar: user?.avatar ?? '',
			isProducer: 1,
		}],
	}), [appSignedInWith, selectedChannel, user]);

	const teamForSelectedChannel: ChannelTeam = useMemo(() => {
		if (!teams || !selectedChannel?.channel || !teams[0]) {
			return baseTeam;
		}

		const returnedTeam = teams.find(
			(team: ChannelTeam) => team.channel === selectedChannel.channel,
		);

		if (returnedTeam) return returnedTeam;
		return baseTeam;
	}, [baseTeam, selectedChannel, teams]);

	return {
		teamForSelectedChannel,
		filteredMeetingsByChannel,
		handleEnterShowDetails,
		showDetailsButtonDisabled,
	};
};

export default useHome;
